import { Box } from '@material-ui/core';
import { useRef } from 'react';

import { InclusionFilterMultiSelectOptions } from '@/components/modules/PricingsList/_shared/constants/InclusionFilterSelectOptions';
import { LendersList } from '@/components/modules/PricingsList/components/Filters/components/LendersList/LendersList';
import { usePricingsFilters } from '@/components/modules/PricingsList/components/Filters/hooks/usePricingsFilters';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { MultiSelectFormField } from '@/components/shared/components/NewContextForms/MultiSelectFormField';
import { SelectFormField } from '@/components/shared/components/NewContextForms/SelectFormField';
import { CustomTextField } from '@/components/shared/components/NewContextForms/shared/CustomTextField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { StyledAccordion } from '@/components/shared/components/StyledAccordion';
import { SubSectionWrapper } from '@/components/shared/components/SubSectionWrapper';
import { Text } from '@/components/shared/components/Text';
import { useUser } from '@/hooks/useUser';

interface FiltersProps {
  setPage: React.Dispatch<React.SetStateAction<number>>;
}

export const Filters = ({ setPage }: FiltersProps) => {
  const { userEntity } = useUser();
  const searchTimeoutRef = useRef<number>();

  if (!userEntity) return null;

  const {
    selectedLender,
    setSelectedLender,
    anyPricingWithGracePeriod,
    anyPricingWithSetupFee,
    InsuranceSelectOptions,
    ProgramSelectOptions,
    PaymentMethodsSelectOptions,
    setSearchPricing,
  } = usePricingsFilters({ userEntity });

  const isAdminOrSuperAdmin = userEntity.isRoleAdminOrSuperAdmin();

  return (
    <>
      <Box display={'flex'} flexDirection={'column'} gridGap={8} mb={3}>
        <SubSectionWrapper title="FILTERS">
          {PaymentMethodsSelectOptions.length > 1 && (
            <Box display={'flex'} flexDirection={'column'} gridGap={12} mb={3}>
              <SelectFormField
                fullWidth
                name={'paymentMethod'}
                selectOptions={PaymentMethodsSelectOptions}
                fieldVariant={FieldVariant.form}
              />
            </Box>
          )}
        </SubSectionWrapper>
        <LendersList selectedLender={selectedLender} setSelectedLender={setSelectedLender} setPage={setPage} />
        <StyledAccordion.Accordion defaultExpanded>
          <StyledAccordion.AccordionSummary
            style={{ marginTop: '12px', display: 'flex', alignItems: 'center' }}
            expandIcon={
              <Font variant="caption" color="textSecondary" style={{ display: 'flex' }}>
                <Icon>expand_more</Icon>
              </Font>
            }
          >
            <Font variant="subtitle2" color="title">
              <Text strong tId={'FILTERS'} />
            </Font>
          </StyledAccordion.AccordionSummary>
          <StyledAccordion.AccordionDetails>
            <Box display={'flex'} width={'100%'} flexDirection={'column'} gridGap={32}>
              <CustomTextField
                label="SEARCH_BY_PRICING_NAME"
                fullWidth
                fieldVariant={FieldVariant.form}
                onChange={(e) => {
                  if (searchTimeoutRef.current) {
                    clearTimeout(searchTimeoutRef.current);
                  }
                  searchTimeoutRef.current = setTimeout(() => {
                    setPage(0);
                    setSearchPricing(e.target.value);
                  }, 300);
                }}
              />
              {InsuranceSelectOptions.length > 0 && (
                <MultiSelectFormField
                  label="INSURANCES"
                  fullWidth
                  name={'insurances'}
                  selectOptions={InsuranceSelectOptions}
                  fieldVariant={FieldVariant.form}
                  placeholder="ALL"
                />
              )}
              {ProgramSelectOptions.length > 1 && isAdminOrSuperAdmin && (
                <MultiSelectFormField
                  label="PROGRAMS"
                  fullWidth
                  name={'programs'}
                  selectOptions={ProgramSelectOptions}
                  fieldVariant={FieldVariant.form}
                  placeholder="ALL"
                />
              )}
              {anyPricingWithGracePeriod && (
                <SelectFormField
                  label="GRACE_PERIOD"
                  fullWidth
                  name={'gracePeriod'}
                  selectOptions={InclusionFilterMultiSelectOptions('GRACE_PERIOD')}
                  fieldVariant={FieldVariant.form}
                />
              )}
              {anyPricingWithSetupFee && (
                <SelectFormField
                  label="SETUP_FEE"
                  fullWidth
                  name={'setupFee'}
                  selectOptions={InclusionFilterMultiSelectOptions('SETUP_FEE')}
                  fieldVariant={FieldVariant.form}
                />
              )}
            </Box>
          </StyledAccordion.AccordionDetails>
        </StyledAccordion.Accordion>
      </Box>
    </>
  );
};
